import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  InputAdornment,
} from '@mui/material';
import { DataGrid, GridColDef, GridToolbar, GridPaginationModel } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { Team, PaginatedResponse } from '../models';
import { teamApi } from '../services/api';

const Teams: React.FC = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [open, setOpen] = useState<boolean>(false);
  const [editingTeam, setEditingTeam] = useState<Partial<Team>>({});
  const [isNew, setIsNew] = useState<boolean>(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const [totalCount, setTotalCount] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const fetchTeams = useCallback(async (page = paginationModel.page, pageSize = paginationModel.pageSize, search = searchTerm) => {
    setLoading(true);
    try {
      const response = await teamApi.getAll({ 
        page: page + 1, // API uses 1-based pagination
        page_size: pageSize,
        search: search
      });
      
      const typedResponse = response.data as PaginatedResponse<Team>;
      setTeams(typedResponse.results);
      setTotalCount(typedResponse.count);
    } catch (error) {
      console.error('Error fetching teams:', error);
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize, searchTerm]);

  const handlePaginationModelChange = useCallback((newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
    fetchTeams(newModel.page, newModel.pageSize);
  }, [fetchTeams]);

  const [isSearching, setIsSearching] = useState(false);
  
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = event.target.value;
    setSearchTerm(newSearchTerm);
    setIsSearching(true);
    
    // Debounce search requests
    const timeoutId = setTimeout(() => {
      fetchTeams(0, paginationModel.pageSize, newSearchTerm);
      setPaginationModel(prev => ({ ...prev, page: 0 })); // Reset to first page on search
      setIsSearching(false);
    }, 500);
    
    return () => clearTimeout(timeoutId);
  };

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  const handleEdit = (team: Team) => {
    setEditingTeam(team);
    setIsNew(false);
    setOpen(true);
  };

  const handleAdd = () => {
    setEditingTeam({
      name: '',
      is_international: false,
      is_test_playing: false,
      is_odi_playing: false,
      is_active: true,
    });
    setIsNew(true);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setEditingTeam((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSave = async () => {
    try {
      if (isNew) {
        await teamApi.create(editingTeam);
      } else {
        await teamApi.update(editingTeam.id!, editingTeam);
      }
      fetchTeams();
      setOpen(false);
    } catch (error) {
      console.error('Error saving team:', error);
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { 
      field: 'image', 
      headerName: 'Logo', 
      width: 80,
      sortable: false,
      renderCell: (params) => {
        // Show image with priority, fallback to flag if available
        const imageUrl = params.row.image || params.row.flag || null;
        return imageUrl ? (
          <img 
            src={imageUrl} 
            alt={params.row.name} 
            style={{ 
              width: '40px', 
              height: '40px', 
              objectFit: 'contain',
              borderRadius: params.row.image ? '4px' : '0px' // Round corners for logos
            }}
          />
        ) : (
          <Box sx={{ 
            width: '40px', 
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: params.row.color || 'grey.300',
            color: 'white',
            borderRadius: '4px'
          }}>
            {params.row.abbreviation?.charAt(0) || params.row.name.charAt(0)}
          </Box>
        );
      }
    },
    { field: 'name', headerName: 'Name', width: 150 },
    { field: 'long_name', headerName: 'Long Name', width: 200 },
    { field: 'abbreviation', headerName: 'Abbreviation', width: 100 },
    { field: 'd_name', headerName: 'D Name', width: 100 },
    { field: 'c_name', headerName: 'C Name', width: 100 },
    { field: 'mt_name', headerName: 'MT Name', width: 100 },
    {
      field: 'is_international',
      headerName: 'International',
      width: 120,
      renderCell: (params) => (params.value ? 'Yes' : 'No'),
    },
    {
      field: 'is_active',
      headerName: 'Active',
      width: 100,
      renderCell: (params) => (params.value ? 'Yes' : 'No'),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 160,
      renderCell: (params) => (
        <Box>
          <IconButton color="primary" onClick={() => handleEdit(params.row)}>
            <EditIcon />
          </IconButton>
          <Button 
            size="small" 
            onClick={() => {
              window.location.href = `/matches?team=${params.row.id}`;
            }}
          >
            View Matches
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ height: "calc(100vh - 100px)", width: "100%" }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4" component="h1">
          Teams
        </Typography>
        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={() => fetchTeams()}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAdd}
          >
            Add Team
          </Button>
        </Box>
      </Box>
      
      {/* Search Box */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search teams by name..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: isSearching ? (
              <InputAdornment position="end">
                <Typography variant="caption" color="text.secondary">
                  Searching...
                </Typography>
              </InputAdornment>
            ) : null,
          }}
          variant="outlined"
          size="small"
        />
      </Box>
      
      <Paper sx={{ height: 'calc(100% - 120px)', width: '100%' }}>
        <DataGrid
          rows={teams}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          slots={{ toolbar: GridToolbar }}
          pageSizeOptions={[25, 50, 100]}
          paginationModel={paginationModel}
          paginationMode="server"
          onPaginationModelChange={handlePaginationModelChange}
          rowCount={totalCount}
          disableColumnFilter
          density="compact"
        />
      </Paper>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{isNew ? 'Add New Team' : 'Edit Team'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              name="name"
              label="Name"
              fullWidth
              value={editingTeam.name || ''}
              onChange={handleInputChange}
              required
            />
            <TextField
              name="long_name"
              label="Long Name"
              fullWidth
              value={editingTeam.long_name || ''}
              onChange={handleInputChange}
            />
            <TextField
              name="abbreviation"
              label="Abbreviation"
              fullWidth
              value={editingTeam.abbreviation || ''}
              onChange={handleInputChange}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    name="is_international"
                    checked={editingTeam.is_international || false}
                    onChange={handleInputChange}
                  />
                }
                label="International"
              />
              <FormControlLabel
                control={
                  <Switch
                    name="is_test_playing"
                    checked={editingTeam.is_test_playing || false}
                    onChange={handleInputChange}
                  />
                }
                label="Test Playing"
              />
              <FormControlLabel
                control={
                  <Switch
                    name="is_odi_playing"
                    checked={editingTeam.is_odi_playing || false}
                    onChange={handleInputChange}
                  />
                }
                label="ODI Playing"
              />
              <FormControlLabel
                control={
                  <Switch
                    name="is_active"
                    checked={editingTeam.is_active || false}
                    onChange={handleInputChange}
                  />
                }
                label="Active"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Teams;
